import { Carousel, Divider } from '@design-system';
import classNames from 'classnames';
import type { GetStaticProps } from 'next';
import ImageNext from 'next/image';
import Link from 'next/link';
import React, { ReactNode, useState } from 'react';
import { render } from 'storyblok-rich-text-react-renderer-ts';

import { AboutNav } from '../components/AboutNav';
import { Image } from '../components/Image';
import { ImageLinkGroup } from '../components/ImageCard/ImageLinkGroup';
import { VideoPlayer } from '../components/Journals/VideoPlayer';
import Layout from '../components/Layout';
import PageSection from '../components/Layout/PageSection';
import { SubscriptionBanner } from '../components/Subscriptions';
import { AuthEntryPoint } from '../features/auth/types';
import { imageLoader } from '../helpers/images';
import { imageOgStaticLoader } from '../helpers/og-image';
import {
  AssetStoryblok,
  MultilinkStoryblok,
  PrintmakingStoryblok,
  ReviewStoryblok as BaseReviewStoryblok,
} from '../types/generated-storyblok';
import { getStoryblokPage, StoryResponse } from '../utils/storyblok';
import { pageTranslations } from '../utils/translations';

// Local types for resolved links in this page
interface ProductLinkResolved extends Omit<MultilinkStoryblok, 'story'> {
  story: {
    name: string;
    content: {
      images: Array<{
        component: string;
        image: AssetStoryblok;
        coverImage: AssetStoryblok;
      }>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  cached_url: string;
}

interface ReviewStoryblokResolved extends Omit<BaseReviewStoryblok, 'product'> {
  product: ProductLinkResolved;
}

interface PrintmakingStoryblokResolved extends Omit<
  PrintmakingStoryblok,
  'section_4_reviews'
> {
  section_4_reviews: ReviewStoryblokResolved[];
}

type PageProps = {
  locale: string;
  locales: string[];
  story: PrintmakingStoryblokResolved;
  ogImageUrl: string;
};

const STYLE_SUBTITLE =
  'text-balance max-w-[448px] text-center mx-auto  text-center text-[20px] font-bold leading-6 md:text-[30px] md:leading-9';

const SectionQuote = ({
  authorLine1,
  authorLine2,
  body,
  imageAlt,
  imageSrc,
}: {
  authorLine1: string;
  authorLine2?: string;
  body: string;
  imageSrc?: string;
  imageAlt?: string;
}) => (
  <PageSection className="max-w-[576px] text-center">
    <blockquote className="my-0 flex flex-col items-center space-y-3">
      {imageSrc && imageAlt ? (
        <ImageNext
          alt={imageAlt}
          className="size-12 rounded-full"
          height={96}
          loader={imageLoader}
          src={imageSrc}
          width={96}
        />
      ) : null}
      <p className="text-md md:text-lg">{body}</p>
      <div className="flex flex-col">
        <span className="aa-prose max-w-none text-neutral-4">
          {authorLine1}
        </span>
        {authorLine2 ? (
          <span className="aa-prose max-w-none text-neutral-4">
            {authorLine2}
          </span>
        ) : null}
      </div>
    </blockquote>
  </PageSection>
);

const SectionDivider = () => (
  <PageSection>
    <Divider />
  </PageSection>
);

const Review = ({
  imageSrc,
  productLink,
  productName,
  review,
}: {
  imageSrc: string;
  productLink: string;
  productName: string;
  review: ReactNode | null;
}) => (
  <blockquote className="flex h-full flex-col space-y-5 rounded-sm border border-divider/20 bg-white p-6">
    <div className="flex flex-col space-y-3">
      <span className="h-5 w-5 text-neutral-4">
        <svg viewBox="0 0 20 20">
          <path
            d="M9.123 6.409V2C3.457 2.198 1 5.17 1 11.56V18h8.123c-.05-1.189-.1-2.328-.1-3.864 0-1.634.05-2.774.1-3.963H4.71v-.346c0-2.527 1.906-3.369 4.413-3.418M10.877 18H19c-.05-1.189-.1-2.328-.1-3.864 0-1.634.05-2.774.1-3.963h-4.412v-.346c0-2.527 1.905-3.369 4.412-3.418V2c-5.666.198-8.123 3.17-8.123 9.56z"
            fill="currentColor"
          />
        </svg>
      </span>
      <p className="text-neutral-6">{review}</p>
    </div>

    <footer className="text-neutral-4">
      Review of{' '}
      <Link
        className="hyperlink"
        href={productLink}
        prefetch={false}
        target="_blank"
      >
        {productName}
      </Link>
    </footer>

    <ImageNext
      alt={productName}
      className="size-30 rounded-sm object-cover"
      height={240}
      loader={imageLoader}
      src={imageSrc}
      width={240}
    />
  </blockquote>
);

const SectionArtistsGallery = ({
  artists,
}: {
  artists: Array<{
    id: string;
    name: string;
    tagline: string;
    videoPoster?: string;
    videoId?: string;
    videoTitle?: string;
    gallery: Array<{
      src: string;
      alt: string;
      id: string;
    }>;
    quoteBody?: string;
    quoteAuthor?: string;
  }>;
}) => {
  const [activeArtist, setActiveArtist] = useState(artists[0].id);
  const selectedArtist = artists.find((artist) => artist.id === activeArtist);

  if (!selectedArtist) return null;

  return (
    <div className="flex flex-col gap-12">
      <ul className="flex gap-2 overflow-x-auto no-scrollbar justify-center-safe">
        {artists.map((artist) => (
          <li key={artist.id} className="shrink-0 first:ml-2 last:mr-2">
            <button
              className={classNames('rounded-full border px-4 py-2', {
                'border-black': activeArtist === artist.id,
              })}
              onClick={() => setActiveArtist(artist.id)}
              type="button"
            >
              {artist.name}
            </button>
          </li>
        ))}
      </ul>

      <PageSection>
        <p className="mx-auto max-w-[448px] text-center">
          {selectedArtist.tagline}
        </p>
      </PageSection>

      <div className="flex gap-3 overflow-x-auto px-5 no-scrollbar sm:mx-auto md:block md:max-w-3xl md:columns-2 lg:max-w-5xl lg:columns-3 xl:max-w-7xl 2xl:max-w-website">
        {selectedArtist.videoId &&
        selectedArtist.videoTitle &&
        selectedArtist.videoPoster ? (
          <div className="aspect-square w-[85%] shrink-0 md:mb-3 md:w-full">
            <VideoPlayer
              baseUrl="https://player.vimeo.com/video/"
              handlePlay={() => {}}
              poster={selectedArtist.videoPoster}
              ratio={{
                sm: '1/1',
              }}
              title={selectedArtist.videoTitle}
              videoId={selectedArtist.videoId}
            />
          </div>
        ) : null}
        {selectedArtist.gallery.map((image) => (
          <div
            key={image.id}
            className="aspect-square w-[85%] shrink-0 sm:aspect-[4/3] md:mb-3 md:w-full sm:[&:nth-child(5)]:aspect-square"
          >
            <ImageNext
              alt={image.alt}
              className="h-full w-full rounded-sm object-cover"
              height={800}
              loader={imageLoader}
              src={image.src}
              width={600}
            />
          </div>
        ))}
        {selectedArtist.quoteBody && selectedArtist.quoteAuthor ? (
          <div className="w-[85%] shrink-0">
            <blockquote className="my-0 flex flex-col space-y-3 p-6">
              <p className="text-md md:text-lg">{selectedArtist.quoteBody}</p>
              <div className="flex flex-col">
                <span className="aa-prose max-w-none text-neutral-4">
                  {selectedArtist.quoteAuthor}
                </span>
              </div>
            </blockquote>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const Page = ({ locale, locales, ogImageUrl, story }: PageProps) => {
  return (
    <Layout
      description={story.seo.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={story.seo.title}
    >
      <AboutNav />

      <main className="my-9 flex flex-col gap-9 md:my-12 md:gap-20">
        {/* hero */}
        <header>
          <PageSection>
            <section className="flex flex-col justify-center gap-5 text-center">
              <h1 className="mx-auto max-w-[678px] text-balance text-[30px] font-bold leading-9 md:text-[60px] md:leading-[60px]">
                {story.header_title}
              </h1>
              <p className="mx-auto max-w-[448px]">{story.header_body}</p>
            </section>
          </PageSection>
        </header>
        {/* media */}
        <PageSection>
          {story.header_video_id && story.header_video_title ? (
            <VideoPlayer
              baseUrl="https://player.vimeo.com/video/"
              handlePlay={() => {}}
              poster={story.header_image.filename}
              ratio={{
                sm: '16/9',
              }}
              title={story.header_video_title}
              videoId={story.header_video_id}
            />
          ) : (
            <Image
              alt={story.header_image.alt || ''}
              className="mx-auto h-full w-full overflow-hidden rounded-sm"
              size={{ sm: { ratio: '16/9', span: 12 } }}
              src={story.header_image.filename}
            />
          )}
        </PageSection>
        <SectionQuote
          authorLine1={story.header_quote_author}
          body={story.header_quote_body}
          imageAlt={story.header_quote_image.alt || story.header_quote_author}
          imageSrc={story.header_quote_image.filename}
        />
        <SectionDivider />
        {/* section_1 */}
        <PageSection>
          <h2 className={STYLE_SUBTITLE}>{story.section_1_heading}</h2>
        </PageSection>
        <SectionArtistsGallery
          artists={story.section_1_artists_gallery.map((item) => ({
            id: item._uid,
            name: item.name,
            tagline: item.tagline,
            videoPoster: item.video_poster?.filename,
            videoId: item.video_id,
            videoTitle: item.video_title,
            quoteBody: item.quote_body,
            quoteAuthor: item.quote_author,
            gallery: item.gallery.map((i) => ({
              src: i.filename,
              alt: i.alt || item.name,
              id: i.id.toString(),
            })),
          }))}
        />
        <SectionDivider />
        {/* section_2 */}
        <PageSection className="flex flex-col gap-5">
          <h2 className={STYLE_SUBTITLE}>{story.section_2_heading}</h2>
          <p className="mx-auto max-w-[448px] text-center">
            {story.section_2_body}
          </p>
        </PageSection>

        <div className="flex gap-3 overflow-x-auto px-5 no-scrollbar justify-center-safe">
          {story.section_2_items.map((item) => (
            <div
              key={item._uid}
              className="flex w-[85%] shrink-0 flex-col gap-3 sm:w-[400px]"
            >
              <div className="w-full">
                <VideoPlayer
                  baseUrl="https://player.vimeo.com/video/"
                  handlePlay={() => {}}
                  poster={item.video_poster.filename}
                  ratio={{
                    sm: '3/4',
                  }}
                  title={item.video_title}
                  videoId={item.video_id}
                />
              </div>
              <div className="flex items-center gap-3">
                <ImageNext
                  alt={item.thumbnail.alt || item.video_title}
                  className="size-16 rounded-sm object-cover"
                  height={128}
                  loader={imageLoader}
                  src={item.thumbnail.filename}
                  width={128}
                />
                <div>
                  <p className="font-bold">{item.thumbnail_title}</p>
                  <p>{item.thumbnail_subtitle}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <SectionQuote
          authorLine1={story.section_2_quote_author}
          body={story.section_2_quote_body}
        />
        <SectionDivider />
        {/* section_3 */}
        <PageSection className="flex flex-col gap-5">
          <h2 className={STYLE_SUBTITLE}>{story.section_3_heading}</h2>
          <p className="mx-auto max-w-[348px] text-center">
            {story.section_3_body}
          </p>
        </PageSection>

        <div className="wrapper-website mx-auto flex gap-3 overflow-x-auto no-scrollbar max-sm:px-4 sm:block sm:columns-2 sm:overflow-auto md:columns-4">
          {story.section_3_gallery.map((image) => (
            <ImageNext
              key={image.id}
              alt={image?.alt ?? 'Gallery image'}
              className="shrink-1 mx-auto aspect-square max-w-[85%] rounded-sm object-cover sm:mb-3 sm:aspect-[4/3] sm:max-w-full md:aspect-[4/3] md:[&:is(:nth-child(2),:nth-child(3),:nth-child(6),:nth-child(7))]:aspect-[3/4] sm:max-md:[&:is(:nth-child(3),:nth-child(6),:nth-child(7),:nth-child(8))]:aspect-[3/4]"
              height={800}
              loader={imageLoader}
              src={image.filename}
              width={800}
            />
          ))}
        </div>

        <PageSection>
          <p className="aa-prose mx-auto text-center">
            <span className="font-bold">Make-Ready</span>
            <br />
            London
            <br />
            <a href="https://www.make-ready.co">make-ready.com</a>
            <br />
            <a href="https://www.instagram.com/make__ready/">
              instagram/make_ready
            </a>
          </p>
        </PageSection>

        <SectionDivider />
        {/* section_4 */}
        <PageSection>
          <h2 className={STYLE_SUBTITLE}>{story.section_4_heading}</h2>
        </PageSection>
        <PageSection fullWidth>
          <Carousel
            // carousel renders default arrow when the headline is not set
            // and ignores the arrows=false prop on the Carousel
            // so we will be better off just setting a space as headline
            // and display custom arrows, should be fixed as part of
            // https://linear.app/avantarte/issue/PTE-2726/splide-carousel-renders-default-arrow-and-ignores-the-arrows-props
            headline=" "
            items={story.section_4_reviews.map((review) => (
              <Review
                key={review._uid}
                imageSrc={
                  review.product.story.content.images[0].component === 'image'
                    ? (review.product.story.content.images[0].image?.filename ??
                      '')
                    : (review.product.story.content.images[0].coverImage
                        ?.filename ?? '')
                }
                productLink={review.product.cached_url}
                productName={review.product.story.name}
                review={render(review.body)}
              />
            ))}
            perPage={4}
          />
        </PageSection>

        <SectionDivider />

        <PageSection>
          <SubscriptionBanner
            buttonLocation="printmaking page"
            image={story.subscription_image.filename}
            source={AuthEntryPoint.PrintmakingPage}
            subtitle={story.subscription_description}
            title={story.subscription_title}
          />
        </PageSection>
        <SectionDivider />
        <PageSection fullWidth>
          <ImageLinkGroup {...(story.links[0] as ImageLinkGroupBlok)} />
        </PageSection>
      </main>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async ({
  locale = 'en',
  locales,
  preview = false,
}) => {
  const page: StoryResponse<{ content: PrintmakingStoryblokResolved }> =
    (await getStoryblokPage('cdn/stories/printmaking', {
      locale,
      preview,
      resolve_links: 'story',
    })) as StoryResponse<{ content: PrintmakingStoryblokResolved }>;

  if (!page) return { notFound: true };

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: page.data.story.content.seo.title,
    imageSrc:
      'https://a.storyblok.com/f/119968/4000x2667/846735f337/parra-atelier-2.jpg',
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
    locale,
  });

  return {
    props: {
      story: page.data.story.content,
      preview,
      locale,
      locales,
      ogImageUrl,
      ...(await pageTranslations(locale, ['og'])),
    },
  };
};

export default Page;
